import { EmergenceAnimationNew } from '@/app/_common/emergence-loader-new'
import type { Turn } from '@/hooks/api/use-turn-status-query'
import { cn } from '@/lib/utils'
import { Circle, CircleCheck, Clock3, TriangleAlert, XCircle } from 'lucide-react'
import React from 'react'
import { AgentInfo } from '../use-turn-parsed'

const AGENT_LOADING_MESSAGE: Partial<Record<string, string>> = {
  webAutomation: 'Searching',
  onlineSearch: 'Searching',
  textAnalysis: 'Analyzing text',
}

type StepStatusHeaderProps = Readonly<{
  status?: Turn['status']
  error?: Turn['error']
  agent: AgentInfo | null
}>

function StepStatusHeader({ status, error, agent }: StepStatusHeaderProps) {
  const { title, icon } = React.useMemo<{ title: string; icon?: React.ReactNode }>(() => {
    if (isTurnStatusLoading(status)) {
      return {
        title: (agent?.agentId && AGENT_LOADING_MESSAGE?.[agent.agentId]) || 'Running...',
        icon: (
          <EmergenceAnimationNew className='relative flex h-6 w-6 items-center justify-center opacity-30 [&>svg]:absolute [&>svg]:m-auto [&>svg]:!h-12 [&>svg]:!w-12' />
        ),
      }
    }
    if (status === 'SUCCESS') return { title: 'Plan completed' }
    if (status === 'FAILURE')
      return { title: `Error${error?.errorCode ? `: ${error.errorCode}` : ''}` }
    if (status === 'CANCELLED') return { title: 'Cancelled' }
    return { title: 'Plan steps' }
  }, [status, error?.errorCode, agent?.agentId])

  return (
    <>
      {icon ?? <StepStatusIcon status={status} />}
      <span className='text-sm font-normal'>{title}</span>
    </>
  )
}

type StepStatusIconProps = Readonly<{
  status?: Turn['status']
  className?: string
}>

function StepStatusIcon({ className, status }: StepStatusIconProps) {
  const icon = React.useMemo(() => {
    if (status === 'FAILURE')
      return <TriangleAlert className='relative -top-px size-4 text-warning-300' />
    if (status === 'SUCCESS')
      return <CircleCheck strokeWidth={2} className='size-4 text-success-300' />
    if (status === 'IN_PROGRESS' || status === 'PLANNING' || status === 'QUEUED')
      return <Clock3 strokeWidth={2} className='size-4 animate-pulse text-warning-300' />
    if (status === 'CANCELLED')
      return <XCircle strokeWidth={2} className='size-4 text-neutral-150' />
    return <Circle strokeWidth={2} className='size-4 text-neutral-150' />
  }, [status])

  return (
    <div
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
        status === 'SUCCESS' && 'bg-success-50',
        (status === 'FAILURE' ||
          status === 'IN_PROGRESS' ||
          status === 'PLANNING' ||
          status === 'QUEUED') &&
          'bg-warning-100',
        (!status || status === 'CANCELLED') && 'bg-neutral-75',
        className,
      )}
    >
      {icon}
    </div>
  )
}

// Helper function
const isTurnStatusLoading = (status?: Turn['status'] | null): boolean =>
  status === 'IN_PROGRESS' || status === 'QUEUED' || status === 'PLANNING'

export { isTurnStatusLoading, StepStatusHeader, StepStatusIcon }
